import { useState } from "react";
import { WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/admin/AdminUI";
import CountryPicker from "@/components/admin/CountryPicker";
import { api } from "@/lib/api";
import { countryName } from "@/lib/countries";
import { REGION_STATUSES } from "@/lib/constants";

// Same split as the server: new lines, commas, semicolons, tabs (a single space stays inside a code, e.g. "K1A 0B6").
// US codes are digits only, so spaces separate them too, and each must be 5 digits.
const parse = (text, country) => {
  const items = text.split(/[\n,;\t]+|\s{2,}/).map((z) => z.trim()).filter(Boolean);
  return country === "US" ? items.flatMap((z) => z.split(/\s+/)) : items;
};
const usInvalid = (codes) => codes.filter((z) => !/^\d{5}$/.test(z));
const List = ({ items, testId }) => <span className="font-mono" data-testid={testId}>{items.join(", ")}</span>;

function Result({ r }) {
  return (
    <div className="cw-tone-success mt-4 space-y-1 rounded-xl px-4 py-3 text-sm" role="status" data-testid="zip-import-result">
      <p className="font-medium">{r.added} postal code{r.added === 1 ? "" : "s"} saved to “{r.region?.label || "Postal code rule"}” ({countryName(r.region?.country)}).</p>
      {r.duplicates_in_paste > 0 && <p>{r.duplicates_in_paste} repeated in the paste (counted once).</p>}
      {r.invalid?.length > 0 && <p className="cw-error">Skipped {r.invalid.length} not valid for {countryName(r.region?.country)}: <List items={r.invalid} testId="zip-import-invalid" /></p>}
      {r.already_in_other_regions?.length > 0 && <p className="cw-warning" data-testid="zip-import-overlap">Also in other rules (the most specific wins): {r.already_in_other_regions.join(", ")}</p>}
    </div>
  );
}

function errorText(err, f, target) {
  if (err.code === "country_mismatch")
    return `“${target?.label || "That rule"}” is for ${countryName(target?.country || "US")}, but you picked ${countryName(f.country)}. Pick ${countryName(target?.country || "US")} or import into a new rule.`;
  return err.message;
}

export default function ZipImport({ regions, onImported }) {
  const [f, setF] = useState({ zips: "", country: "", status: "waitlist", label: "", region_id: "new" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const set = (k) => (v) => { setF((s) => ({ ...s, [k]: v })); setError(null); };
  const zipRegions = regions.filter((r) => r.type === "zip");
  const target = zipRegions.find((r) => r.id === f.region_id);
  const codes = parse(f.zips, f.country);
  const badUs = f.country === "US" ? usInvalid(codes) : [];

  const pickRegion = (id) => {
    const r = zipRegions.find((x) => x.id === id);
    setF((s) => ({ ...s, region_id: id, country: r ? r.country || "US" : s.country }));
    setError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setRes(null);
    try {
      const body = { zips: f.zips, country: f.country, status: f.status, ...(target ? { region_id: target.id } : { label: f.label.trim() || null }) };
      const r = await api("/admin/regions/import-zips", { body });
      setRes(r);
      onImported(r.region, !!target);
      setF((s) => ({ ...s, zips: "" }));
    } catch (err) {
      setError({ text: errorText(err, f, target), invalid: err.data?.invalid || [] });
    } finally { setBusy(false); }
  };

  return (
    <Panel className="p-6" data-testid="zip-import-panel">
      <h2 className="font-semibold">Postal codes</h2>
      <p className="cw-soft mt-1 text-sm">Bulk import for one country. One per line, or separated by commas, semicolons or tabs. Any format, e.g. K1A 0B6; US codes must be 5 digits.</p>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label htmlFor="zi-country">Country</Label>
            <CountryPicker id="zi-country" value={f.country} onChange={set("country")} className="mt-2" testId="zip-import-country-picker" invalid={error && !f.country} /></div>
          <div><Label htmlFor="zi-into">Into</Label>
            <Select value={f.region_id} onValueChange={pickRegion}>
              <SelectTrigger id="zi-into" className="mt-2" data-testid="zip-import-region-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="new">A new postal code rule</SelectItem>{zipRegions.map((r) => <SelectItem key={r.id} value={r.id}>{r.label || r.values.slice(0, 3).join(", ")} · {r.country || "US"}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div><Label htmlFor="zi-zips" className="sr-only">Postal codes</Label>
          <Textarea id="zi-zips" rows={4} value={f.zips} onChange={(e) => set("zips")(e.target.value)} placeholder={"K1A 0B6, M5V 2T6\n53202"} aria-invalid={badUs.length > 0} className="font-mono text-sm" data-testid="zip-import-input" />
          <p className="cw-muted mt-1 text-xs" data-testid="zip-import-count">{codes.length} value{codes.length === 1 ? "" : "s"} pasted</p>
          {badUs.length > 0 && <p className="cw-error mt-1 text-xs" role="alert">US codes must be 5 digits: <List items={badUs} testId="zip-import-us-invalid" /></p>}</div>
        {!target && <div className="grid gap-4 sm:grid-cols-2">
          <div><Label htmlFor="zi-status">Status</Label>
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger id="zi-status" className="mt-2" data-testid="zip-import-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REGION_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label htmlFor="zi-label">Label</Label><Input id="zi-label" value={f.label} onChange={(e) => set("label")(e.target.value)} placeholder="Ottawa core" className="mt-2" data-testid="zip-import-label-input" /></div>
        </div>}
        {error && <div className="cw-tone-error flex items-start gap-2 rounded-xl px-3 py-2 text-sm" role="alert" data-testid="zip-import-error">
          <WarningOctagon size={16} className="mt-0.5 shrink-0" />
          <div><p>{error.text}</p>{error.invalid.length > 0 && <p className="mt-1">Not valid: <List items={error.invalid} testId="zip-import-error-invalid" /></p>}</div></div>}
        <Button type="submit" disabled={!codes.length || !f.country || badUs.length > 0 || busy} data-testid="zip-import-submit-button">{busy ? "Importing…" : "Import postal codes"}</Button>
      </form>
      {res && <Result r={res} />}
    </Panel>
  );
}
